import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { useIdpAuth } from '@/context/IdpAuth/IdpAuthContext';
import { useUser } from '@/context/UserContext';

import Loader from '@/components/Loader';

import { resolverDestinoPosLogin, urlDoDestino } from '@/lib/auth/destinoPosLogin';
import { vaiParaFranquias } from '@/lib/auth/franquias';
import { SEM_CONTA_PATH } from '@/lib/idp/config';
import { destinoPosLogin as deepLinkValido } from '@/lib/idp/paths';

/** O `state.from` que o `RequireAuth` usa, montado a partir do `returnTo`. */
function fromDe(returnTo: string | undefined) {
  const deepLink = deepLinkValido(returnTo);
  if (deepLink === undefined) return undefined;
  const { pathname, search, hash } = new URL(deepLink, window.location.origin);
  return { from: { pathname, search, hash } };
}

/**
 * A rota `/auth/callback`, cadastrada como `redirect_uri` do client
 * `sapron-frontend` no realm.
 *
 * Ela nao troca o code: quem faz isso e o `IdpAuthProvider`, que roda acima de
 * toda a arvore e fecha o fluxo antes de qualquer tela montar. O que sobra aqui
 * e levar o usuario ao destino — o mesmo do login por senha
 * (`resolverDestinoPosLogin`). Rota interna vai pelo react-router, e nao por
 * `window.location`: um reload jogaria fora os tokens, que so vivem em memoria.
 * A Wallet e o Franquias saem do app direto daqui, sem passar por uma tela do Sapron que dispararia chamadas a API no meio do redirecionamento.
 */
const AuthCallback = () => {
  const { status, returnTo } = useIdpAuth();
  const { userInformation } = useUser();
  const navigate = useNavigate();
  const decidiuRef = useRef(false);

  useEffect(() => {
    if (status === 'loading' || decidiuRef.current) return;

    if (status === 'no-vinculo') {
      decidiuRef.current = true;
      navigate(SEM_CONTA_PATH, { replace: true });
      return;
    }

    if (status === 'anonymous' || userInformation === null || userInformation === undefined) {
      // Tentativa que falhou (inclusive a silenciosa) ou API que recusou o
      // token: volta ao login guardando o deep-link, como o `RequireAuth` faz.
      decidiuRef.current = true;
      navigate('/login', { replace: true, state: fromDe(returnTo) });
      return;
    }

    decidiuRef.current = true;
    const usuario = userInformation;
    const irAoDestino = async () => {
      // A flag do Franquias e a mesma do login por senha (`vaiParaFranquias`),
      // avaliada pelo e-mail do usuario; sem e-mail ela conta como desligada.
      const destino = resolverDestinoPosLogin({
        usuario,
        deepLink: returnTo,
        franquias: usuario.email ? await vaiParaFranquias(usuario, usuario.email) : false,
      });
      const externa = urlDoDestino(destino, {
        walletUrl: import.meta.env.REACT_APP_PUBLIC_WALLET_URL,
        franquiasUrl: import.meta.env.REACT_APP_PUBLIC_FRANQUIAS_URL,
        comSessaoDoIdp: true,
      });
      if (externa !== undefined) {
        // Destino vem da env; nenhum token vai na URL, so a marca de SSO.
        // nosemgrep: sast-rules.pr-blocking.javascript.browser.security.js-open-redirect-from-function
        window.location.assign(externa);
        return;
      }
      if (destino.tipo === 'rota') navigate(destino.rota, { replace: true });
    };
    irAoDestino();
  }, [navigate, returnTo, status, userInformation]);

  return <Loader />;
};

export default AuthCallback;
